/**
 * Tabbed video player component for reviewing screen recordings and best-practice videos.
 *
 * @module components/sim/takeSim/VideoPlayer
 * @author Snowfly
 * @version 1.0.0
 */
import React, {createRef} from "react";
import PropTypes from "prop-types";
import {Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Tabs, Tooltip,} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import { TransformWrapper, TransformComponent, useTransformInit } from "react-zoom-pan-pinch";
import useStyles from "./VideoPlayer.js";
import Tab from "@material-ui/core/Tab";
import {connect} from "react-redux";
import permissionCheck from "../../../utils/permissionCheck";
import SimsPermissions from "../../../permissions/sims";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import SaveIcon from "@material-ui/icons/Save";
import StarsIcon from "@material-ui/icons/Stars";

/**
 * VideoPlayer is a React component designed to handle video playback using multiple tabs. It
 * includes essential controls for playing, pausing, and resizing videos, as well as managing
 * multiple video sources within a tabbed interface.
 *
 * @param {Object} props - The properties provided to the VideoPlayer component.
 * @param {string} props.videoTitle - The title of the video currently displayed in the player.
 * @param {Object} props.video - The video object containing metadata and video stream URLs.
 * @param {number} props.videoIndex - The index of a specific video within the video object used
 *     for playback.
 * @param {boolean} props.open - Determines whether the video player is currently opened.
 * @param {Function} props.onClose - Callback function invoked when the video player is closed.
 * @param {string} props.loadingPoster - The URL of the loading poster image used while videos are
 *     buffered or loading.
 * @param {Function} props.onSaveScreenShareVideo - Callback function for saving the current
 *     screen-share video.
 * @param {Function} props.onDownloadScreenShareVideo - Callback function for downloading the
 *     current screen-share video.
 * @param {boolean} props.videoDetailsFetched - Indicates whether the video details have been
 *     successfully fetched from the API or source.
 * @param {Function} props.setAlert - Callback function to display alerts or notifications in the
 *     application.
 */
const VideoPlayer = ({
    videoTitle,
    video,
    videoIndex,
    open,
    onClose,
    loadingPoster,
    onSaveScreenShareVideo,
    onDownloadScreenShareVideo,
    videoDetailsFetched,
    setAlert,
}) => {
    const minVideoZoomLevel = 0.8;
    const maxVideoZoomLevel = 10.0;
    const videoZoomStep = 0.5;
    const classes = useStyles();

    const player = React.useRef([createRef(), createRef()]);
    const playerState = React.useRef([createRef(), createRef()]);
    const currentTab = React.useRef(0);
    const currentVideo = React.useRef(null);
    const videoPaused = React.useRef(true);
    const title = React.useRef('');
    const [hideSaveVideoButton, setHideSaveVideoButton] = React.useState(false);
    const [hideBestPracticeVideoButton, setHideBestPracticeVideoButton] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);

    const [videoUrls, setVideoUrls] = React.useState([]);
    const [videoRefs, setVideoRefs] = React.useState([createRef(), createRef()]); // eslint-disable-line no-unused-vars
    const [closing, setClosing] = React.useState(false);
    const [videoToPlay, setVideoToPlay] = React.useState(-1);

    React.useEffect(
        () => {
            setIsOpen(open);
            if (open) {
                setActiveTab(document.getElementById('videoTab-0'), 0);
            }
        },
        [open]
    );

    React.useEffect(
        () => {
            if (closing || !video) {
                setVideoToPlay(-1);
                return;
            }

            if (
                (!videoUrls?.length && video?.urls?.length) ||
                (JSON.stringify(videoUrls) !== JSON.stringify(video?.urls)) ||
                (videoToPlay > 0)
            ) {
                let newUrls = video.urls;
                switch (videoToPlay) {
                    case -1:
                    case 0:
                        break;
                    default:
                        newUrls = video.last_five[videoToPlay]?.urls;
                }
                //console.log('Setting video urls to %o', newUrls)
                setVideoUrls(newUrls);
            }

            videoRefs.forEach((videoRef) => {
                if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                }
            });

            currentVideo.current = videoRefs[currentTab.current].current;
            updateTabs(currentTab.current).then(() => {
                if (videoToPlay > 0) {
                    setHideSaveVideoButton(!!video.last_five[videoToPlay].saved_on);
                    setHideBestPracticeVideoButton(!!video.last_five[videoToPlay].marked_best_practice_on);
                } else {
                    setHideSaveVideoButton(!!video.saved_on);
                    setHideBestPracticeVideoButton(!!video.marked_best_practice_on);
                }
            });
        },
        [video, videoToPlay, closing, videoRefs, videoUrls]
    );

    React.useEffect(
        () => {
            if (videoUrls?.length) {
                for (const [index, url] of videoUrls.entries()) {
                    if (videoRefs[index]?.current?.src) {
                        videoRefs[index].current.src = url;
                    }
                }

                const startPlay = () => {
                    if (currentVideo.current) {
                        currentVideo.current.play();
                    } else {
                        currentVideo.current = videoRefs[currentTab.current].current;
                        setTimeout(startPlay, 500);
                    }
                };

                setTimeout(startPlay, 500);
            }
        },
        [videoUrls, videoRefs]
    );

    React.useEffect(
        () => {
            setVideoToPlay(videoIndex === null ? -1 : videoIndex);
        },
        [videoIndex]
    );

    React.useEffect(
        () => {
            title.current = videoTitle ?? '';
            console.log('Changed video player title to %s', title.current);
        },
        [videoTitle]
    );

    /**
     * Cleans up video resources and resets application state.
     * This asynchronous function pauses all videos referenced in the `videoRefs` array, resets
     * their playback position to the start, and clears the current video and tab references. It
     * ensures that the cleanup process runs only if the application is not already in a "closing"
     * state.
     *
     * @async
     * @function
     * @returns {Promise<void>} A promise that resolves when the cleanup process is complete.
     */
    const destroy = async() => {
        if (closing) {
            return;
        }

        for await (const videoRef of videoRefs) {
            if (videoRef.current) {
                await videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
        currentVideo.current = null;
        currentTab.current = 0;
        setClosing(false);
    };

    /**
     * Clears event listeners added to the current video element.
     *
     * This function removes the 'play', 'pause', 'waiting', and 'ratechange' event handlers
     * from the current video element, if it is defined and references a valid DOM element.
     */
    const clearVideoHandlers = () => {
        if (currentVideo?.current) {
            currentVideo.current.removeEventListener('play', handleOnPlay);
            currentVideo.current.removeEventListener('pause', handleOnPause);
            currentVideo.current.removeEventListener('waiting', handleOnWaiting);
            currentVideo.current.removeEventListener('ratechange', handleOnRateChange);
        }
    };

    /**
     * Sets event handlers for video-related events on the current video element.
     *
     * Adds listeners for the following events if the `currentVideo.current` element exists:
     * - 'play': Fires the `handleOnPlay` callback when the video starts playing.
     * - 'pause': Fires the `handleOnPause` callback when the video is paused.
     * - 'waiting': Fires the `handleOnWaiting` callback when the video is buffering.
     * - 'ratechange': Fires the `handleOnRateChange` callback when the playback rate changes.
     */
    const setVideoHandlers = () => {
        if (currentVideo?.current) {
            currentVideo.current.addEventListener('play', handleOnPlay);
            currentVideo.current.addEventListener('pause', handleOnPause);
            currentVideo.current.addEventListener('waiting', handleOnWaiting);
            currentVideo.current.addEventListener('ratechange', handleOnRateChange);
        }
    };

    /**
     * Asynchronously resizes video elements to fit the dimensions of their parent container.
     *
     * This function queries a parent container using its CSS class match and calculates
     * its height. It then adjusts the height of video elements, setting it dynamically
     * based on the parent container's height minus a 3em offset.
     *
     * The function expects videoRefs to be available and properly initialized, where
     * each videoRef is an object with a `current` property representing the video DOM element.
     *
     * If the parent container is not found, a default height of 800px is used as a fallback.
     * The modifications are only applied if the `current.style` property exists for each videoRef.
     */
    const resizeVideoElements = async () => {
        // Resize the video elements to fit the parent container
        let parentNode = document.querySelector('[class*="makeStyles-videoPlayer-"] [class*="makeStyles-panelParent-"]'),
            parentHeight = parentNode?.clientHeight ?? 800;

        if (parentNode) {
            videoRefs.forEach((videoRef) => {
                if (videoRef?.current?.style) {
                    videoRef.current.style.height=`calc(${parentHeight}px - 3em)`;
                }
            });
        }
    };

    /**
     * Updates the active video tab and controls video playback behavior across tabs.
     *
     * This function is responsible for changing the active video tab, pausing the current video if
     * it's playing, clearing existing video event handlers, updating video visibility, and
     * resuming playback of the new tab's video, if applicable. It ensures a smooth transition and
     * playback management for videos across multiple tabs.
     *
     * @async
     * @param {number} tabIndex - The index of the tab to switch to, determining which video to
     *     activate.
     * @returns {Promise<void>} Resolves when all asynchronous actions, such as pausing and playing
     *     videos, are completed.
     */
    const updateTabs = async (tabIndex) => {
        let currentPosition = 0;

        currentTab.current = tabIndex;

        if (currentVideo?.current) {
            if (!videoPaused.current) {
                try {
                    await currentVideo.current.pause();
                } catch (e) {
                    console.log(e);
                }
                videoPaused.current = true;
            }
            clearVideoHandlers();

            currentPosition = parseFloat(currentVideo.current?.currentTime);
            if (isNaN(currentPosition)) {
                currentPosition = 0;
            }
            currentVideo.current = null;
        }

        videoRefs.forEach((videoRef) => {
            if (videoRef?.current?.style) {
                videoRef.current.style.display = 'none';
                videoRef.current.muted = true;
            }
        });

        if (videoRefs[tabIndex]?.current?.style) {
            videoRefs[tabIndex].current.style.display = 'block';
            currentVideo.current = videoRefs[tabIndex].current;
            if (!currentVideo.current) {
                setAlert('There was a problem loading the video', 'error');
                onClose();
                return;
            }
            currentVideo.current.currentTime = currentPosition + 0.100;
            currentVideo.current.muted = false;

            setVideoHandlers();
            if (videoPaused.current) {
                try {
                    await currentVideo.current.play();
                } catch (e) {
                    console.log(e);
                }
            }
        }
    };

    /**
     * Asynchronously sets the active tab in a tabbed navigation system.
     *
     * This function updates the selected tab, hides all associated tab content panels,
     * and displays the content panel corresponding to the newly selected tab. It also
     * adjusts the "selected" indicator's position to align with the new active tab.
     *
     * @param {HTMLElement} tabElement - The tab element or button being selected.
     * @param {number} tabIndex - The index of the tab to be activated.
     *
     * @returns {Promise<void>} A promise that resolves when the action is completed.
     */
    const setActiveTab = async (tabElement, tabIndex) => {
        if (!tabElement) {
            return;
        }

        let parent = tabElement;
        while (parent && parent.role !== 'tablist') {
            parent = parent.parentElement;
        }
        parent.querySelectorAll('button[role="tab"]').forEach((button, index) => {
            // This hides the tab contents panel
            document.getElementById(`displayPanel-${index}`).style.display = 'none';
            if (index === tabIndex) {
                button.classList.add('Mui-selected');
                button.attributes.ariaSelected = true;
            } else {
                button.classList.remove('Mui-selected');
                button.attributes.ariaSelected = false;
            }
        });

        // This moves the "Selected" indicator to the current tab
        const indicator = parent.parentElement.querySelector('.MuiTabs-indicator');
        indicator.style.left = (parseFloat(indicator.style.width) * currentTab.current) + 'px';

        // This shows the right tab content panel
        document.getElementById(`displayPanel-${currentTab.current}`).style.display = 'block';
    };

    /**
     * An asynchronous function responsible for handling the closing of a video player or related
     * UI component. This function pauses the current video if it is playing, resets its playback
     * time, removes assigned handlers, triggers an optional closing callback, and performs cleanup
     * actions such as resetting the active tab and destroying resources.
     *
     * @async
     * @function handleClose
     * @param {Object} evt - The event object associated with the close action.
     * @returns {Promise<void>} A promise that resolves when all closing actions have been
     *     completed.
     */
    const handleClose = async (/* evt */) => {
        setClosing(true);

        if (currentVideo?.current) {
            await currentVideo.current.pause();
            currentVideo.current.currentTime = 0;
        }
        clearVideoHandlers();

        onClose && onClose();
        setClosing(false);
        videoPaused.current = true;
        await setActiveTab(null, -1);
        await destroy();
    };

    /**
     * Asynchronous event handler triggered when a play event occurs on a media player.
     *
     * This function updates the playback state of the associated media by setting
     * the `videoPaused.current` flag to `false`. It indicates that the media is
     * now playing. The function can also optionally utilize the event object
     * supplied as a parameter for further handling or debugging, if necessary.
     *
     * @param {Event} evt - The event object associated with the play event.
     */
    const handleOnPlay = async(/* evt */) => {
        //console.log('Play event: %o', evt)
        videoPaused.current = false;
        //console.log('Paused (onPlay) = %o', videoPaused.current);
    };

    /**
     * An asynchronous event handler function that is triggered when a pause event occurs.
     * Sets the `videoPaused` reference to true, indicating that the video is currently paused.
     *
     * @param {Event} evt - The event object associated with the pause event.
     */
    const handleOnPause = async(/* evt */) => {
        videoPaused.current = true;
    };

    /**
     * An asynchronous event handler for waiting events on a video element.
     *
     * This function triggers when the waiting event is fired, allowing for
     * adjustments or synchronization of video elements if needed. Primarily,
     * it ensures synchronization of offsets between video elements in the
     * videoRefs array.
     *
     * @param {Event} evt - The waiting event object triggered by the video element.
     */
    const handleOnWaiting = async(/* evt */) => {
        //console.log('Waiting event = %o', evt);
        if (videoRefs[1]?.current) {
            videoRefs[1].current.offset = videoRefs[0].current.offset;
        }
    };

    /**
     * Asynchronous function to handle the rate change event for a video player.
     * It synchronizes the playback rate across multiple video players based on the
     * playback rate of the target video player triggering the event.
     *
     * @param {Event} evt - The event object triggered by a rate change in the video player.
     *                      This contains information about which video player initiated the event.
     */
    const handleOnRateChange = async (evt) => {
        let curV = videoRefs.find((v) => v.current.id === evt.currentTarget.id);

        for await (const v of videoRefs) {
            if (v !== curV) {
                v.current.playbackRate = curV.current.playbackRate;
            }
        }
    };

    /**
     * Handles the tab change event and updates the active tab accordingly.
     *
     * @function
     * @async
     * @param {Object} evt - The event object triggered by the tab change.
     * @param {number|string} newVal - The new value or index representing the active tab.
     * @returns {Promise<void>} Resolves when the active tab is successfully updated.
     */
    const handleTabChange = async (evt, newVal) => {
        const evtTarget = evt.target;

        await updateTabs(newVal);
        await setActiveTab(evtTarget, newVal);
    };

    /**
     * Handles the click event for downloading a screen share video.
     *
     * This function is triggered when the download button for a screen share video
     * is clicked. It invokes the callback `onDownloadScreenShareVideo` with the event
     * object `evt` if the callback is defined.
     *
     * @async
     * @function
     * @param {Event} evt - The event object representing the click action.
     */
    const handleVideoDownloadClick = async (evt) => {
        onDownloadScreenShareVideo && onDownloadScreenShareVideo(evt);
    };

    /**
     * Footer component responsible for rendering video action buttons and managing their state.
     *
     * This component handles the display and functionality of various video-related actions, such
     * as:
     * - Downloading a screen share video.
     * - Saving a screen share video.
     * - Marking a screen share video as a "Best Practice."
     *
     * State Management:
     * - Stores and updates state for displaying success messages when an action is performed.
     * - Tracks whether the video has been saved or marked as a best practice, ensuring buttons
     * reflect the current video state.
     *
     * Props:
     * - `onDownloadScreenShareVideo {function}`: Callback invoked when the download button is
     * clicked. It is responsible for handling the download action.
     * - `hideSaveVideoButton {boolean}`: Determines whether the "Save Screen Recording" button
     * should be hidden.
     * - `hideBestPracticeVideoButton {boolean}`: Determines whether the "Best Practice" button
     * should be hidden.
     * - `onSaveScreenShareVideo {function}`: Callback invoked when a video save action is
     * triggered. It processes and manages the save operation.
     *
     * Behavior:
     * - Conditionally renders buttons based on user permissions, video state, and provided props.
     * - Displays tooltips with contextual messages about each button’s functionality or current
     * state.
     * - Provides feedback to users with success messages when actions (e.g., saving, marking as
     * best practice) are performed.
     *
     * Dependencies:
     * - Utilizes React hooks (`useState`, `useEffect`) for managing component state and side
     * effects.
     * - Depends on external utility functions (e.g., `permissionCheck`) for permission validation.
     * - Leverages UI framework components such as `Button` and `Tooltip` to render styled
     * interactive elements.
     *
     * Returns:
     * - Renders a footer section containing buttons for video actions (conditionally displayed)
     * and success messages if applicable.
     */
    const Footer = ({
        onDownloadScreenShareVideo,
        hideSaveVideoButton,
        hideBestPracticeVideoButton,
        onSaveScreenShareVideo,
        videoToPlay
    }) => {
        const [saveMessage, setSaveMessage] = React.useState('');
        const [saveMessageShow, setSaveMessageShow] = React.useState(false);
        const [isVideoSaved, setIsVideoSaved] = React.useState(null);
        const [isVideoBestPractice, setIsVideoBestPractice] = React.useState(null);

        React.useEffect(
            () => {
                if (null === isVideoBestPractice) {
                    setIsVideoBestPractice(hideBestPracticeVideoButton);
                }
                if (null === isVideoSaved) {
                    setIsVideoSaved(hideSaveVideoButton);
                }
            },
            [
                hideBestPracticeVideoButton,
                hideSaveVideoButton,
                isVideoBestPractice,
                isVideoSaved
            ]
        );

        React.useEffect(
            () => {
                if (videoToPlay > 0) {
                    if (video.last_five[videoToPlay + 1]) {
                        setIsVideoBestPractice(!!video.last_five[videoToPlay + 1].marked_best_practice_on);
                        setIsVideoSaved(!!video.last_five[videoToPlay + 1].saved_on);
                    }
                } else {
                    if (video) {
                        setIsVideoBestPractice(!!video.marked_best_practice_on);
                        setIsVideoSaved(!!video.saved_on);
                    }
                }
            },
            [videoToPlay]
        );

        /**
         * Handles the saving of a screen share video action.
         * This function processes the save operation, triggers appropriate user notifications,
         * and updates state based on the video being marked as "Best Practice" or simply being
         * saved.
         *
         * @async
         * @function handleSaveScreenShareVideo
         * @param {Event} ev - The event object triggered by the save action, containing the target
         *     element properties.
         * @returns {void}
         * @throws {Error} Will throw an error if there is a failure in the
         *     `onSaveScreenShareVideo` operation.
         */
        const handleSaveScreenShareVideo = async (ev) => {
            const targetId = ev.currentTarget.id;
            if (onSaveScreenShareVideo) {
                let result = await onSaveScreenShareVideo(ev);
                if (!result.success) {
                    setAlert('There was a problem saving the screen share video', 'error');
                    return;
                }
            }
            const isBestPractice = targetId === 'bestPracticeScreenRecording';

            setSaveMessage(
                isBestPractice ?
                    'Video was marked as Best Practice' :
                    'Video was Saved'
            );
            setSaveMessageShow(true);
            if (isBestPractice) {
                //setVideoMarkedAsBestPractice(true);
                setIsVideoBestPractice(true);
            }
            //setVideoMarkedAsSaved(true);
            setIsVideoSaved(true);

            setTimeout(() => setSaveMessageShow(false), 2500);
        };

        /**
         * A function to conditionally render a video download button component.
         *
         * This function checks user permissions and determines if the video download feature
         * should be displayed. If the user has the appropriate permission and video details
         * are fetched or a video object exists, it renders a button inside a tooltip that
         * allows users to download a screen recording video. Otherwise, it returns null.
         *
         * Dependencies:
         * - Utilizes `permissionCheck` to verify if the user has the required permissions.
         * - References `SimsPermissions` object for permission details.
         * - Uses `Tooltip` and `Button` components from the UI framework for rendering.
         * - References `VideoLibraryIcon` as the icon for the button.
         *
         * Key Parameters:
         * - `SimsPermissions.download_video.view.task`: Represents the specific task permission
         * being checked.
         * - `SimsPermissions.download_video.view.type`: Represents the type of permission being
         * checked.
         *
         * Conditions:
         * - Renders the download button if:
         *   1. User has the required download video permission (`canDownloadVideos` is true).
         *   2. Either `videoDetailsFetched` or `video` exists.
         * - Returns `null` if the conditions are not met.
         *
         * Behavior:
         * - The tooltip displays the text "Download Screen Share Video" when hovered.
         * - Clicking the button triggers the `onDownloadScreenShareVideo` function, which
         * presumably handles the download action.
         */
        const showVideoDownload = () => {
            const canDownloadVideos = permissionCheck(
                SimsPermissions.download_video.view.task,
                SimsPermissions.download_video.view.type
            );

            if (canDownloadVideos && (videoDetailsFetched || video)) {
                return (
                    <Tooltip
                        title={"Download Screen Share Video"}
                        placement="bottom"
                    >
                    <span className={classes.editSpan}>
                        <Button
                            variant="contained"
                            className={classes.preview}
                            size="small"
                            onClick={onDownloadScreenShareVideo}
                            href={'#'}
                            target={"_blank"}
                            startIcon={(<VideoLibraryIcon/>)}
                        >Download Screen Recording</Button>
                    </span>
                    </Tooltip>
                );
            }

            return null;
        };

        /**
         * Determines whether to display the video save buttons and renders them if applicable.
         *
         * The function checks for the conditions allowing video saving and renders two buttons:
         * 1. "Save Screen Recording" button - Adds functionality to save the screen recording
         * video.
         * 2. "Mark as Best Practice" button - Allows marking the screen recording as a best
         * practice.
         *
         * The buttons are conditionally disabled based on the current state of the video (e.g.,
         * if the video is already saved or marked as best practice). Tooltips are displayed to
         * provide user context about the state and functionality of the buttons.
         *
         * @function
         * @returns {React.Element | null} Rendered video save buttons wrapped in tooltips, or null
         *     if conditions are not met.
         */
        const showVideoSaveButtons = () => {
            const canDownloadVideos = true;

            if (canDownloadVideos && (videoDetailsFetched || video)) {
                return (
                    <>
                        <Tooltip
                            title={isVideoSaved ? "The Video has been saved" : "Save Screen Share Video"}
                            placement="bottom"
                        >
                        <span className={classes.editSpan}>
                            <Button
                                id={'saveScreenRecording'}
                                variant="contained"
                                className={classes.preview}
                                size="small"
                                onClick={handleSaveScreenShareVideo}
                                startIcon={(<SaveIcon/>)}
                                disabled={isVideoSaved}
                            >Save Screen Recording</Button>
                        </span>
                        </Tooltip>
                        <Tooltip
                            title={isVideoBestPractice ? "The recording has already been marked as Best Practice" : "Marks the Screen Sharing Video as Best Practice"}
                            placement="bottom"
                        >
                        <span className={classes.editSpan}>
                            <Button
                                id={'bestPracticeScreenRecording'}
                                variant="contained"
                                className={classes.preview}
                                size="small"
                                onClick={handleSaveScreenShareVideo}
                                startIcon={(<StarsIcon/>)}
                                disabled={/*hideBestPracticeVideoButton|| */isVideoBestPractice}
                            >Mark as Best Practice</Button>
                        </span>
                        </Tooltip>
                    </>
                )
            } else {
                return null;
            }
        };

        return (
            <div className={classes.dialogFooter} style={{paddingInline: '1em'}}>
                {showVideoDownload()}
                {showVideoSaveButtons()}
                {saveMessageShow && (<p className={classes.success}>{saveMessage}</p>)}
            </div>
        );
    };

    Footer.propTypes = {
        onDownloadScreenShareVideo: PropTypes.func,
        hideSaveVideoButton: PropTypes.bool,
        hideBestPracticeVideoButton: PropTypes.bool,
        onSaveScreenShareVideo: PropTypes.func,
        videoToPlay: PropTypes.number,
    };

    /**
     * A functional React component responsible for rendering and managing a video player within a
     * transformation context. Allows for zooming, panning, and video playback controls. Supports
     * multiple tabs and dynamic management of video elements tied to tab identifiers.
     *
     * @param {Object} props - The props object.
     * @param {string} props.url - The URL of the video to be displayed.
     * @param {string} props.tabId - The identifier for the current video tab.
     * @param {Function} props.zoomIn - A function to handle zooming into the video.
     * @param {Function} props.zoomOut - A function to handle zooming out of the video.
     * @param {Function} props.setTransform - A function to set the transformation state of the
     *     video view.
     * @param {Function} props.resetTransform - A function to reset the transformation state to its
     *     default.
     *
     * @returns {JSX.Element} The rendered player component with video and transformation controls.
     */
    // eslint-disable-next-line no-unused-vars
    const PlayerChild = ({url, tabId, zoomIn, zoomOut, setTransform, resetTransform, ...rest}) => {
        useTransformInit(
            ({state, instance}) => {
                /**
                 * Asynchronous function responsible for initializing video player instances.
                 *
                 * This function iterates through a collection of video players, setting up
                 * relevant references, state values, and handling essential configurations
                 * required for video playback. It also performs error handling for video
                 * loading and playback issues and ensures proper resizing of video elements.
                 *
                 * Key functionalities include:
                 * - Assigning the player instance and state values for each tab or player item.
                 * - Initializing and verifying the current video reference.
                 * - Handling video loading errors and displaying appropriate alerts.
                 * - Managing video playback and ensuring the video starts from the beginning.
                 * - Resizing video elements to align with necessary display requirements.
                 *
                 * This function is essential for ensuring that the video players are correctly
                 * set up and functional before interaction.
                 *
                 * @async
                 */
                const doInit = async () => {
                    if (player.current) {
                        for await (let [tabId, playerItem] of player.current.entries()) {
                            playerItem.current = instance;
                            playerState.current[tabId].current = state;
                            if (!currentVideo.current) {
                                currentVideo.current = videoRefs[tabId].current;
                                if (!currentVideo.current) {
                                    setAlert('There was a problem loading the video', 'error');
                                    await handleClose();
                                    return;
                                }
                                currentVideo.current.onerror = async (/* evt */) => {
                                    setAlert(
                                        'There was a problem loading the video: ' +
                                        JSON.stringify(currentVideo.current.error),
                                        'error'
                                    );
                                    await handleClose();
                                };

                                if (!closing) {
                                    currentVideo.current.load();
                                    currentVideo.current.currentTime = 0;
                                    await currentVideo.current.play();
                                }
                            }
                        }
                        await resizeVideoElements();
                    }
                };

                /**
                 * Represents an asynchronous function that triggers the destruction process.
                 * This function is used to execute the `destroy` operation and ensures
                 * it is performed in an asynchronous manner.
                 *
                 * @function
                 * @async
                 * @returns {Promise<void>} Resolves when the destruction process is complete.
                 */
                const doDestroy = async () => {
                    await destroy();
                };

                doInit();

                return async () => {
                    await doDestroy();
                }
            }
        );

        /**
         * Handles the start of the panning gesture on a zoomable and pannable component.
         * This function updates the transform state of the current player's instance based on the
         * panning event details.
         *
         * @param {Object} evt - The event object containing the current state of the panning
         *     gesture.
         * @param {number} evt.state.scale - The current scale factor of the component.
         * @param {number} evt.state.x - The current X translation of the component.
         * @param {number} evt.state.y - The current Y translation of the component.
         */
        const handlePanningStart = (evt) => {
            if (player[currentTab.current]?.current) {
                player[currentTab.current].current.setTransformState(evt.state.scale, evt.state.x, evt.state.y);
            }
        };

        /**
         * Event handler for stopping the panning operation.
         *
         * Processes the panning state when the panning movement concludes
         * and updates the transform state of the current player instance
         * associated with the active tab.
         *
         * @param {object} evt - The event object containing the panning state.
         * @param {number} evt.state.scale - The current scale value after panning.
         * @param {number} evt.state.x - The current x-coordinate position after panning.
         * @param {number} evt.state.y - The current y-coordinate position after panning.
         */
        const handlePanningStop = (evt) => {
            if (player[currentTab.current]?.current) {
                player[currentTab.current].current.setTransformState(evt.state.scale, evt.state.x, evt.state.y);
            }
        };

        /**
         * Handles the video click event to toggle the video's paused state.
         *
         * This function updates the `videoPaused` state by toggling its current value
         * when a video click event is triggered. It is asynchronous in case further
         * implementations require asynchronous operations.
         *
         * @param {Event} evt - The event object associated with the video click.
         */
        const onVideoClick = async (/* evt */) => {
            videoPaused.current = !videoPaused.current;
        };

        /**
         * Handles the "click" event on a video element in capture phase.
         *
         * This asynchronous event handler prevents the default action associated
         * with the event and also stops it from propagating to other elements
         * in the DOM. It is intended to be used during the capture phase of event
         * propagation, ensuring that it intercepts the event before any listeners
         * in the target or bubble phase.
         *
         * @param {Event} evt - The event object representing the click action.
         * @returns {Promise<void>} A promise that resolves when the handler completes execution.
         */
        const onVideoClickCapture = async (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
        };

        return (
            <div id={`displayPanel-${tabId}`}
                 className={classes.previewPanel}
                 style={{display: parseInt(tabId) ? 'none' : 'block'}}
            >
                <div className={classes.zoomBar}>
                    <IconButton aria-label="Zoom In"
                                onClick={(/* evt */) => zoomIn()}
                                disabled={(playerState[tabId]?.scale ?? 0) === maxVideoZoomLevel}
                    >
                        <ZoomInIcon/>
                    </IconButton>
                    <IconButton aria-label="Zoom Out"
                                onClick={(/* evt */) => zoomOut()}
                                disabled={(playerState[tabId]?.scale ?? 0) === minVideoZoomLevel}
                    >
                        <ZoomOutIcon/>
                    </IconButton>
                    <IconButton aria-label="Reset Zoom"
                                onClick={(/* evt */) => resetTransform()}
                                disabled={(playerState[tabId]?.scale ?? 0) === minVideoZoomLevel}
                    >
                        <RotateLeftIcon/>
                    </IconButton>
                </div>
                <TransformComponent
                    id={`transform-${tabId}`}
                    onPanningStart={handlePanningStart}
                    onPanningStop={handlePanningStop}
                >
                    <div id={`videoTabPanel-${tabId}`} key={`videoTabPanel-${tabId}`}>
                        <video
                            id={`video_${tabId}`}
                            ref={videoRefs[tabId]}
                            className={classes.videoModal}
                            poster={loadingPoster ?? ''}
                            key={tabId}
                            controls
                            onClick={onVideoClick}
                            onClickCapture={onVideoClickCapture}
                        >
                            <source src={url} type="video/mp4"/>
                        </video>
                    </div>
                </TransformComponent>
            </div>
        );
    };

    PlayerChild.propTypes = {
        url: PropTypes.string,
        tabId: PropTypes.string,
        zoomIn: PropTypes.func,
        zoomOut: PropTypes.func,
        setTransform: PropTypes.func,
        resetTransform: PropTypes.func,
    };

    return (
        <Dialog onClose={handleClose}
                open={isOpen}
                fullWidth
                className={classes.videoDialog}
        >
            <DialogTitle className={classes.previewDialogTitle}>
                Screen Recording {title.current}
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={handleClose}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.videoPlayer}>
                <div className={classes.previewDialog}>
                    <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                        <Tabs value={''+(currentTab.current ?? 0)}
                              onChange={handleTabChange}
                              aria-label={`View video ${currentTab.current+1}`}
                        >
                            {videoUrls?.map((url, i) => (
                                <Tab value={''+i} id={`videoTab-${i}`}
                                     key={`videoTab-${i}`}
                                     label={`Screen ${i + 1}`}
                                />
                            ))}
                        </Tabs>
                    </Box>
                    <Box sx={{borderTop: 1, borderColor: 'divider' }} className={classes.panelParent}>
                        {videoUrls?.map((url, tabId) => (
                            <TransformWrapper
                                id={`wrapper${tabId}`}
                                key={`wrapper${tabId}`}
                                initialScale={1}
                                limitToBounds={true}
                                centerZoomedOut={true}
                                centerOnInit={true}
                                minScale={minVideoZoomLevel}
                                maxScale={maxVideoZoomLevel}
                                panning={{excluded: ['input']}}
                                doubleClick={{disabled: true}}
                                wheel={{step: videoZoomStep}}
                            >
                                {(params) => <PlayerChild {...params}
                                                          className={classes.previewDialog}
                                                          url={url}
                                                          tabId={''+tabId}
                                                          id={`playerChild${tabId}`}
                                                          key={`playerChild${tabId}`}
                                />}
                            </TransformWrapper>
                        ))}
                    </Box>
                </div>
                <Footer hideBestPracticeVideoButton={hideBestPracticeVideoButton}
                        hideSaveVideoButton={hideSaveVideoButton}
                        onDownloadScreenShareVideo={handleVideoDownloadClick}
                        onSaveScreenShareVideo={onSaveScreenShareVideo}
                        videoToPlay={videoToPlay}
                />
            </DialogContent>
        </Dialog>
    );
};

/**
 * PropTypes definition for the VideoPlayer component.
 *
 * This defines the expected types and requirements for the properties
 * that the VideoPlayer component accepts.
 *
 * The properties typically include configurations for video playback,
 * such as the source URL of the video, autoplay settings, and other
 * customizable options to enhance the user experience.
 */
VideoPlayer.propTypes = {
    video: PropTypes.object,
    videoIndex: PropTypes.number,
    videoTitle: PropTypes.string,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    loadingPoster: PropTypes.string,
    onSaveScreenShareVideo: PropTypes.func,
    onDownloadScreenShareVideo: PropTypes.func,
    videoDetailsFetched: PropTypes.bool,
    setAlert: PropTypes.func,
};

/**
 * Maps the relevant state from the Redux store to the props of a React component.
 *
 * @function
 * @name mapStateToProps
 * @param {Object} state - The current state of the Redux store.
 * @returns {Object} - An object containing the `video` property derived from the `screen_share_video` field within `state.sim.userResult`.
 */
const mapStateToProps = (state) => ({
    video: state.sim.userResult.screen_share_video,
});

export default connect(mapStateToProps)(VideoPlayer);
